import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
    AUCTION_START_DATE,
    AUCTION_DAYS,
    TOTAL_DAYS,
    SOL_PRICE_USD,
    TOTAL_AUCTION_TOKENS,
    DAILY_ALLOCATION,
    getDailyAllocation,
    getBonusMultiplier,
    CIRCULATING_SUPPLY,
    START_PRICE
} from '../utils/constants';
import { 
    getCurrentTargetDate, 
    getDayEndDate, 
    getDayStartDate
} from '../utils/timerUtils';
import { getSimulatedData, TOTAL_SIMULATED_USERS, TOTAL_SIMULATED_SOL } from '../utils/simulatedData';
import priceCalculator from '../utils/priceCalculator';
import { getDayStatus, getDayStatusByNumber } from '../utils/auctionCore';
import { createStatsCalculator } from '../utils/statsCalculator';

// Create the context
export const AuctionStateContext = createContext();

export const useAuctionState = () => {
    const context = useContext(AuctionStateContext);
    if (!context) {
        throw new Error('useAuctionState must be used within an AuctionStateProvider');
    }
    return context;
}

// Helper function to calculate current day
const calculateCurrentDay = () => {
    const now = new Date();
    
    // If auction hasn't started yet, return day 1
    if (now < AUCTION_START_DATE) {
        return 1;
    }
    
    // Calculate days since auction start
    const diffTime = Math.abs(now - AUCTION_START_DATE);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Current day is days since start + 1
    return Math.min(diffDays + 1, TOTAL_DAYS);
};

export const AuctionStateProvider = ({ children }) => {
    // Core auction state
    const [currentDay, setCurrentDay] = useState(calculateCurrentDay());
    const [selectedDay, setSelectedDay] = useState(currentDay);
    const [auctionState, setAuctionState] = useState({
        totalSolRaised: TOTAL_SIMULATED_SOL,
        totalParticipants: TOTAL_SIMULATED_USERS,
        dailyAllocations: {},
        userContributions: {}
    });
    
    // Stats derived from auction state
    const [stats, setStats] = useState({
        totalSolRaised: TOTAL_SIMULATED_SOL,
        currentPrice: 0,
        dexPrice: 0,
        moonshot: 0,
        marketCap: 0
    });

    // Initialize auction state with simulated data
    useEffect(() => {
        // Only initialize once
        if (Object.keys(auctionState.dailyAllocations).length > 0) {
            return;
        }
        
        // Initialize daily allocations
        const StatsCalculator = createStatsCalculator();
        StatsCalculator.init();
        
        // Calculate base JINU allocation per day
        const TOTAL_JINU = 37500000; // 37.5M JINU for auction pool
        const baseJinuPerDay = TOTAL_JINU / AUCTION_DAYS;
        
        // Calculate daily allocations with ±35% variance using consistent seed
        const dailyAllocations = Array(TOTAL_DAYS).fill(null).map((_, index) => {
            const day = index + 1;
            
            // Get simulated data
            const simData = getSimulatedData(day);
            
            // Calculate JINU allocation with ±35% variance using consistent seed
            const varianceSeed = Math.sin(day * 200) * 10000;
            const variance = ((varianceSeed - Math.floor(varianceSeed)) * 0.7) - 0.35; // Random between -0.35 and 0.35
            const jinuAllocation = baseJinuPerDay * (1 + variance);
            
            return {
                day,
                totalAmount: simData.sol || 0,
                totalUsers: simData.users || 0,
                jinuAllocation,
                jinuVariance: variance * 100,
                bonusMultiplier: getBonusMultiplier(day),
                startDate: getDayStartDate(day),
                endDate: getDayEndDate(day)
            };
        });

        // Convert to object format for state
        const dailyAllocationsObj = dailyAllocations.reduce((acc, allocation) => {
            acc[allocation.day] = allocation;
            return acc;
        }, {});
        
        // Update state with simulated data
        setAuctionState(prevState => ({
            ...prevState,
            dailyAllocations: dailyAllocationsObj,
            totalSolRaised: TOTAL_SIMULATED_SOL
        }));
        
        // Calculate initial stats
        const initialStats = priceCalculator.calculate(TOTAL_SIMULATED_SOL);
        setStats(initialStats);
    }, []);

    // Update current day based on time
    useEffect(() => {
        const timer = setInterval(() => {
            const newCurrentDay = calculateCurrentDay();
            if (newCurrentDay !== currentDay) {
                setCurrentDay(newCurrentDay);
                setSelectedDay(newCurrentDay);
            }
        }, 60000); // Check every minute
        
        return () => clearInterval(timer);
    }, [currentDay]);

    // Calculate auction stats based on total SOL raised
    const calculateAuctionStats = useCallback(() => {
        const totalSolRaised = auctionState.totalSolRaised || 0;
        return priceCalculator.calculate(totalSolRaised);
    }, [auctionState.totalSolRaised]);

    // Update stats when auction state changes
    useEffect(() => {
        const newStats = calculateAuctionStats();
        setStats(newStats);
    }, [auctionState.totalSolRaised, calculateAuctionStats]);

    // Get day allocation
    const getDayAllocation = useCallback((day) => {
        return auctionState.dailyAllocations[day] || null;
    }, [auctionState.dailyAllocations]);

    // Get day information
    const getDayInfo = useCallback((day) => {
        const dayStatus = getDayStatusByNumber(day, currentDay);
        const dayStart = getDayStartDate(day);
        const dayEnd = getDayEndDate(day);
        const allocation = getDayAllocation(day);
        
        return {
            day,
            status: dayStatus,
            startDate: dayStart,
            endDate: dayEnd,
            allocation
        };
    }, [currentDay, getDayAllocation]);

    // Get all days information
    const getAllDaysInfo = useCallback(() => {
        return Array.from({ length: TOTAL_DAYS }, (_, i) => i + 1).map(getDayInfo);
    }, [getDayInfo]);
    
    // Calculate daily values based on day number
    const calculateDailyValues = useCallback((day) => {
        const { users, sol } = getSimulatedData(day);
        const allocation = getDailyAllocation(day);
        
        return {
            totalAmount: sol,
            totalUsers: users,
            simulatedParticipants: users,
            allocation
        };
    }, [getDailyAllocation]);
    
    // Get bonus badge based on multiplier
    const getBonusBadge = useCallback((bonus) => {
        if (bonus >= 2) return { label: "2x Bonus", color: "red.500" };
        if (bonus >= 1.5) return { label: "1.5x Bonus", color: "orange.500" };
        if (bonus >= 1.25) return { label: "1.25x Bonus", color: "yellow.500" };
        if (bonus > 1) return { label: `${bonus}x Bonus`, color: "green.500" };
        return { label: "No Bonus", color: "gray.500" };
    }, []);
    
    // Update total SOL raised
    const updateTotalSolRaised = useCallback((newTotal) => {
        setAuctionState(prevState => ({
            ...prevState,
            totalSolRaised: newTotal
        }));
        
        // Update stats based on new total
        const newStats = priceCalculator.calculate(newTotal);
        setStats(newStats);
    }, []);

    return (
        <AuctionStateContext.Provider value={{
            // Constants
            TOTAL_DAYS,
            SOL_PRICE_USD,
            TOTAL_AUCTION_TOKENS,
            DAILY_ALLOCATION,
            startDate: AUCTION_START_DATE,
            startPrice: START_PRICE,
            circulatingSupply: CIRCULATING_SUPPLY,
            
            // State
            currentDay,
            selectedDay,
            setSelectedDay,
            auctionState,
            setAuctionState,
            
            // Stats
            totalSolRaised: stats.totalSolRaised,
            currentPrice: stats.currentPrice,
            dexPrice: stats.dexPrice,
            moonshot: stats.moonshot,
            marketCap: stats.marketCap,
            
            // Methods
            getDayInfo,
            getAllDaysInfo,
            getDayAllocation,
            calculateAuctionStats,
            calculateDailyValues,
            getBonusBadge,
            updateTotalSolRaised
        }}>
            {children}
        </AuctionStateContext.Provider>
    );
};
